/**
 * Annotation ↔ server + bus sync.
 *
 * - `useAnnotationSync(sessionId, bus)` subscribes to inbound annotation
 *   envelopes and reflects them into the Zustand store. On the operator
 *   side, the toolbar calls `publishAnnotation` to persist + broadcast.
 *
 * - REST POST and bus broadcast happen in parallel; the server dedups by
 *   `envelope_msg_id` so the same envelope being delivered twice (rare,
 *   but possible if the data-channel reconnects) doesn't produce a phantom
 *   row.
 */
import { useEffect } from "react";

import { ApiPaths, api } from "./api";
import {
  newAnnotationId,
  useAnnotationStore,
} from "./annotation";
import type { CommandBus, EnvelopeIdentity } from "./commandBus";
import { getFieldSessionToken } from "./fieldSession";
import type {
  Annotation,
  AnnotationGeometry,
  AnnotationKind,
} from "./types";


/** Subscribe an active CommandBus to annotation/* topics for the LIVE
 *  overlay store. Evidence-bound strokes (annotation.evidence != null)
 *  are skipped here — they're consumed by `useEvidenceAnnotationSync`
 *  inside the EvidenceAnnotator instead, so the live overlay never
 *  picks up post-capture re-annotation strokes. */
export function useAnnotationSync(bus: CommandBus | null, sessionId: string): void {
  useEffect(() => {
    if (!bus) return;
    const offCreate = bus.on<{ annotation: Annotation }, void>(
      "annotation/draw.create",
      ({ payload }) => {
        if (payload.annotation.evidence !== null) return;
        useAnnotationStore.getState().upsert(payload.annotation);
      },
    );
    const offClear = bus.on<unknown, void>("annotation/clear", () => {
      useAnnotationStore.getState().clear();
    });
    const offRemove = bus.on<{ id: string }, void>(
      "annotation/draw.remove",
      ({ payload }) => useAnnotationStore.getState().remove(payload.id),
    );

    return () => {
      offCreate();
      offClear();
      offRemove();
    };
    void sessionId;
  }, [bus, sessionId]);
}


/** Subscribe to inbound annotation/draw.create envelopes scoped to a
 *  single Evidence id. Used by the EvidenceAnnotator so peer strokes on
 *  the same captured still appear live during a collaborative review.
 *
 *  Multiple subscribers per topic are fine — `CommandBus.on` is
 *  additive, and `useAnnotationSync` already has an early-return for
 *  evidence-bound rows so it won't double-handle. */
export function useEvidenceAnnotationSync(
  bus: CommandBus | null,
  evidenceId: string | null,
  onCreate: (annotation: Annotation) => void,
): void {
  useEffect(() => {
    if (!bus || !evidenceId) return;
    const off = bus.on<{ annotation: Annotation }, void>(
      "annotation/draw.create",
      ({ payload }) => {
        if (payload.annotation.evidence === evidenceId) {
          onCreate(payload.annotation);
        }
      },
    );
    return off;
  }, [bus, evidenceId, onCreate]);
}


export type DraftInput = {
  kind: AnnotationKind;
  geometry: AnnotationGeometry;
  color: string;
  stroke_width?: number;
  label?: string;
  /** When set, the new row is born already-bound to this Evidence —
   *  collaborative re-annotation flow. The server stamps `cleared_at`
   *  on creation so the row never enters the live overlay set. */
  evidence_id?: string;
  /** Pre-minted annotation id. The author pins this on `pointerdown`
   *  so the live-draft envelopes broadcast during the gesture carry
   *  the same id as the eventual `draw.create` envelope — peers
   *  swap the transient draft for the persisted row with no flicker.
   *  Optional: if omitted, the helper mints a fresh UUID. */
  id?: string;
};


/**
 * Persist a new annotation + broadcast it. Returns the server's
 * canonical row (which is the same id we sent for optimistic UI).
 */
export async function publishAnnotation(
  bus: CommandBus | null,
  self: EnvelopeIdentity,
  sessionId: string,
  draft: DraftInput,
): Promise<Annotation> {
  const id = draft.id ?? newAnnotationId();
  const envelope_msg_id = `msg_${id}`;
  const body: Record<string, unknown> = {
    id,
    kind: draft.kind,
    vector_jsonb: draft.geometry,
    color: draft.color,
    stroke_width: draft.stroke_width ?? 0.006,
    label: draft.label ?? "",
    envelope_msg_id,
  };
  if (draft.evidence_id) body.evidence_id = draft.evidence_id;

  // Decide auth: field path attaches the X-Field-Session-Token;
  // operator path falls through to the JWT interceptor on api.ts.
  const fieldToken = getFieldSessionToken();
  const headers = fieldToken
    ? { "X-Field-Session-Token": fieldToken }
    : undefined;

  const res = await api.post<Annotation>(
    ApiPaths.sessionAnnotations(sessionId),
    body,
    headers ? { headers } : undefined,
  );
  const annotation = res.data;
  // Only the LIVE overlay store mirrors the row locally. Evidence-bound
  // rows belong to an EvidenceAnnotator's own state; the broadcast below
  // tells peers to update their own annotator instance via
  // useEvidenceAnnotationSync.
  if (annotation.evidence === null) {
    useAnnotationStore.getState().upsert(annotation);
    // Phase 1B-polish — push to the undo stack so Cmd/Ctrl-Z can target
    // this stroke. Only LIVE strokes are undoable from the operator UI
    // (evidence-bound strokes have their own EvidenceAnnotator).
    useAnnotationStore.getState().pushOwnAnnotation(annotation.id);
  }

  // Best-effort broadcast — the local view already shows it optimistically;
  // the peer's view updates from this envelope. If the bus isn't ready yet
  // we silently skip; the peer can always refetch via REST.
  if (bus) {
    void bus.send({
      topic: "annotation",
      type: "draw.create",
      payload: { annotation },
      requires_ack: false,
    }).catch(() => {});
    void self;
  }

  return annotation;
}


export async function clearAnnotations(
  bus: CommandBus | null,
  sessionId: string,
): Promise<void> {
  await api.post(ApiPaths.sessionAnnotationsClear(sessionId), {});
  useAnnotationStore.getState().clear();
  if (bus) {
    void bus.send({
      topic: "annotation",
      type: "clear",
      payload: {},
      requires_ack: false,
    }).catch(() => {});
  }
}


/** Phase 1B-polish — per-annotation undo. Targets the most-recently
 *  added local stroke (top of the store's undoStack). Performs the
 *  DELETE + drops the row from the live overlay + broadcasts a
 *  `draw.remove` envelope so peers retract their copy too.
 *
 *  Returns the undone annotation id, or `null` when the stack is empty.
 */
export async function undoOwnAnnotation(
  bus: CommandBus | null,
  sessionId: string,
): Promise<string | null> {
  const store = useAnnotationStore.getState();
  const id = store.popOwnAnnotation();
  if (!id) return null;

  // Optimistic local removal so the UI updates immediately.
  store.remove(id);

  const fieldToken = getFieldSessionToken();
  const headers = fieldToken
    ? { "X-Field-Session-Token": fieldToken }
    : undefined;
  try {
    await api.delete(
      ApiPaths.sessionAnnotationDetail(sessionId, id),
      headers ? { headers } : undefined,
    );
  } catch {
    // If the network call fails the audit chain stays whole on the
    // server side; the local optimism is the only loss. We deliberately
    // don't re-insert — a stale ghost-on-network-fail is more
    // confusing than the lost undo.
  }

  if (bus) {
    void bus.send({
      topic: "annotation",
      type: "draw.remove",
      payload: { id },
      requires_ack: false,
    }).catch(() => {});
  }
  return id;
}


/** Add a new annotation directly bound to an existing captured Evidence
 *  row. One RPC — the server stamps `evidence` + `cleared_at` on
 *  creation so the row never enters the live overlay set.
 *
 *  When `bus` is provided, broadcasts the new row over the data channel
 *  so peers viewing the same EvidenceAnnotator update in real time. If
 *  the viewer is opened OUTSIDE the live LiveKit room (e.g. reviewing a
 *  past session), pass `bus=null` — the local optimistic render is
 *  enough.
 */
export async function createAnnotationForEvidence(
  sessionId: string,
  evidenceId: string,
  draft: DraftInput,
  bus: CommandBus | null = null,
): Promise<Annotation> {
  const id = draft.id ?? newAnnotationId();
  const envelope_msg_id = `msg_${id}`;
  const body: Record<string, unknown> = {
    id,
    kind: draft.kind,
    vector_jsonb: draft.geometry,
    color: draft.color,
    stroke_width: draft.stroke_width ?? 0.006,
    label: draft.label ?? "",
    envelope_msg_id,
    evidence_id: evidenceId,
  };
  // Field path attaches the field-session token for dual-auth; operator
  // path falls through to the JWT interceptor.
  const fieldToken = getFieldSessionToken();
  const headers = fieldToken
    ? { "X-Field-Session-Token": fieldToken }
    : undefined;

  // Diagnostic log — surfaces in the field's browser console so we
  // can verify both auth paths actually fire. Print only the header
  // PRESENCE (not the token value) to avoid leaking credentials.
  // eslint-disable-next-line no-console
  console.info(
    "[createAnnotationForEvidence] sess=%s ev=%s id=%s field_token=%s",
    sessionId,
    evidenceId,
    id,
    fieldToken ? "present" : "missing",
  );

  try {
    const created = await api.post<Annotation>(
      ApiPaths.sessionAnnotations(sessionId),
      body,
      headers ? { headers } : undefined,
    );

    if (bus) {
      void bus
        .send({
          topic: "annotation",
          type: "draw.create",
          payload: { annotation: created.data },
          requires_ack: false,
        })
        .catch(() => {});
    }
    return created.data;
  } catch (err) {
    // Re-throw with extra context so the caller's onError surfaces a
    // useful message instead of axios's terse default. The field's
    // EvidenceAnnotator displays this in an alert banner so the user
    // sees the actual reason (401 = stale field token, 400 = bad
    // payload, etc.) rather than silently losing strokes.
    // eslint-disable-next-line no-console
    console.error("[createAnnotationForEvidence] POST failed", err);
    throw err;
  }
}


/** Bind the given live annotations to a freeze-frame Evidence row, then
 * tell peers to clear local state. Called by SnapshotButton after the
 * field's capture acks. */
export async function bindAnnotationsToEvidence(
  bus: CommandBus | null,
  evidenceId: string,
  annotationIds: string[],
): Promise<void> {
  if (annotationIds.length === 0) return;
  await api.post(ApiPaths.evidenceAnnotationsBind(evidenceId), {
    annotation_ids: annotationIds,
  });
  useAnnotationStore.getState().clear();
  if (bus) {
    void bus.send({
      topic: "annotation",
      type: "clear",
      payload: { reason: "bound_to_evidence", evidence_id: evidenceId },
      requires_ack: false,
    }).catch(() => {});
  }
}